import { SolutionScenario } from '@/types/scenarios/solution';
import { Plane, Phone, Calendar, MapPin, Utensils, Bed, Star, Clock } from 'lucide-react';

export const travelHospitality: SolutionScenario = {
  meta: {
    id: 'travel-hospitality',
    slug: 'travel-hospitality',
    name: 'Travel & Hospitality',
    tagline: 'Genies that handle every traveller question so your team can run the trip',
    description: "Give your clients access to information on itineraries, recommendations, destination guides or insurance support in any language, 24/7 wherever they are.",
    market: 'Travel & Hospitality',
    subMarkets: ['Hotels & Resorts', 'Travel Agencies', 'Vacation Rentals', 'Tour Operators', 'Restaurants'],
    specificNeeds: [
      'Guest concierge services',
      'Reservation inquiries and modifications',
      'Local recommendations and directions',
      'Room service and amenity requests',
      'Check-in/check-out assistance',
      'Travel booking support'
    ],
    commonRoles: ['Front Desk Staff', 'Concierge', 'Reservation Agents', 'Guest Services'],
    marketSize: '$1.5 trillion global market',
    isFeatured: true
  },

  hero: {
    headline: 'Concierge support, around the clock',
    headlineGradient: 'Concierge support,',
    headlineRest: 'around the clock',
    subheadline: "Give your clients access to information on itineraries, recommendations, destination guides or insurance support in any language, 24/7 wherever they are.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'interactive-faq',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'guest-concierge',
      name: 'Virtual Concierge',
      description: 'Instant answers to guest questions about amenities, local attractions, and services',
      department: 'Guest Services',
      icon: MapPin,
      playbookIds: ['interactive-faq', 'proactive-welcome'],
      benefits: [
        '24/7 concierge availability',
        'Local recommendations',
        'Directions and transit help',
        'Multilingual support'
      ]
    },
    {
      id: 'reservations',
      name: 'Reservation Management',
      description: 'Handle booking inquiries, modifications, and cancellations seamlessly',
      department: 'Reservations',
      icon: Calendar,
      playbookIds: ['appointment-scheduler', 'lead-capture'],
      benefits: [
        'Real-time availability',
        'Instant confirmations',
        'Modification handling',
        'Upsell opportunities'
      ]
    },
    {
      id: 'room-service',
      name: 'Room Service & Requests',
      description: 'Take orders and handle guest requests without phone wait times',
      department: 'Operations',
      icon: Utensils,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Voice-activated ordering',
        'Special requests handling',
        'Housekeeping requests',
        'Maintenance reporting'
      ]
    },
    {
      id: 'travel-booking',
      name: 'Travel & Tour Booking',
      description: 'Help guests book excursions, transportation, and local experiences',
      department: 'Concierge',
      icon: Plane,
      playbookIds: ['appointment-scheduler', 'lead-capture'],
      benefits: [
        'Tour and activity booking',
        'Transportation arrangements',
        'Restaurant reservations',
        'Experience recommendations'
      ]
    },
    {
      id: 'loyalty-rewards',
      name: 'Loyalty & Rewards Support',
      description: 'Handle loyalty program inquiries, points balance, and reward redemptions',
      department: 'Guest Services',
      icon: Star,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Points balance inquiries',
        'Reward redemption assistance',
        'Tier status information',
        'Member benefit explanations'
      ]
    },
    {
      id: 'checkin-checkout',
      name: 'Check-in & Check-out Assistance',
      description: 'Streamline arrival and departure processes with voice-guided support',
      department: 'Front Desk',
      icon: Clock,
      playbookIds: ['interactive-faq', 'proactive-welcome'],
      benefits: [
        'Express check-in guidance',
        'Late check-out requests',
        'Folio review assistance',
        'Departure scheduling'
      ]
    }
  ],

  challenges: [
    {
      title: 'Guest Expectations',
      description: 'Modern travelers expect instant, personalized service at any hour',
      impact: 'high',
      affectedAreas: ['Guest satisfaction', 'Reviews and ratings', 'Repeat bookings']
    },
    {
      title: 'Staff Coverage Gaps',
      description: 'Difficult to maintain full concierge coverage during all shifts',
      impact: 'high',
      affectedAreas: ['Service quality', 'Labor costs', 'Guest experience']
    },
    {
      title: 'Language Barriers',
      description: 'International guests need support in their preferred language',
      impact: 'medium',
      affectedAreas: ['Guest communication', 'Service quality', 'Market reach']
    },
    {
      title: 'Repetitive Inquiries',
      description: 'Staff time consumed by common questions about WiFi, checkout times, amenities',
      impact: 'medium',
      affectedAreas: ['Staff efficiency', 'Guest wait times', 'Service quality']
    }
  ],

  solutions: [
    {
      challenge: 'Inconsistent service availability',
      solution: '24/7 Voice Genie concierge for instant guest assistance',
      implementation: 'Deploy QR codes in rooms and common areas for voice-activated help',
      expectedResults: [
        'Round-the-clock guest support',
        '90% of common questions answered instantly',
        'Consistent service quality'
      ]
    },
    {
      challenge: 'Staff overwhelmed with routine requests',
      solution: 'Voice Genie handles FAQs and simple requests automatically',
      implementation: 'Automate WiFi info, checkout times, amenity questions, and basic requests',
      expectedResults: [
        '55% reduction in front desk calls',
        'Staff focus on high-touch interactions',
        'Faster response to complex needs'
      ]
    },
    {
      challenge: 'Missed upsell opportunities',
      solution: 'Proactive Voice Genie suggestions for services and experiences',
      implementation: 'Recommend spa treatments, dining, tours based on guest context',
      expectedResults: [
        '25% increase in ancillary revenue',
        'Higher guest engagement',
        'Improved experience scores'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'Opera PMS',
        category: 'Property Management',
        description: 'Oracle hospitality management system',
        required: false,
        popularityScore: 92
      },
      {
        name: 'Cloudbeds',
        category: 'Property Management',
        description: 'Hospitality management platform',
        required: false,
        popularityScore: 85
      },
      {
        name: 'Mews',
        category: 'Property Management',
        description: 'Modern hospitality cloud',
        required: false,
        popularityScore: 82
      },
      {
        name: 'Sabre',
        category: 'Travel Distribution',
        description: 'Travel technology platform',
        required: false,
        popularityScore: 88
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Guest relationship management',
        required: false,
        popularityScore: 95
      },
      {
        name: 'Twilio',
        category: 'Communications',
        description: 'SMS and voice communications',
        required: false,
        popularityScore: 90
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '400%',
    adoptionRate: '92% guest satisfaction',
    customerSatisfaction: '4.8/5'
  },

  demoConfig: {
    firstMessage: "Welcome! I'm your virtual concierge. I can help you with local recommendations, room service, amenity information, or anything else to make your stay exceptional. How can I assist you?",
    systemPrompt: "You are a friendly and knowledgeable hotel concierge. Help guests with local recommendations, amenity information, service requests, and travel arrangements. Be warm, professional, and proactive in anticipating guest needs. Offer personalized suggestions when appropriate.",
    marketContext: "Hotel or resort providing guest services and concierge assistance",
    exampleScenarios: [
      "What's the WiFi password?",
      "Can you recommend a good restaurant nearby?",
      "I'd like to order room service",
      "What time is checkout?"
    ]
  },

  caseStudies: [
    {
      companyName: 'Seaside Resort Collection',
      companySize: '4 boutique hotels',
      location: 'Coastal California',
      challenge: 'Limited concierge hours, inconsistent guest service, missed revenue opportunities',
      implementation: 'Deployed Help Genie via in-room QR codes for 24/7 virtual concierge',
      timeline: '10 days',
      results: [
        {
          metric: 'Guest Satisfaction',
          value: '4.9/5',
          improvement: 'Up from 4.3'
        },
        {
          metric: 'Front Desk Calls',
          value: '-48%',
          improvement: 'Reduction'
        },
        {
          metric: 'Ancillary Revenue',
          value: '+32%',
          improvement: 'Spa, dining, tours'
        },
        {
          metric: 'Review Scores',
          value: '+0.6',
          improvement: 'Average increase'
        }
      ],
      quote: {
        text: "Our guests love having a concierge available at 3 AM when they want dinner recommendations or have questions. It's elevated our boutique experience without adding staff.",
        author: 'Maria Santos',
        title: 'Director of Guest Experience'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Hospitality Voice Genie Playbook',
      description: 'Implementing voice concierge for hotels and resorts',
      url: '/resources/hospitality-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Elevating Guest Experience with Voice Genies',
      description: 'How leading hotels increase satisfaction and revenue',
      url: '/webinars/hospitality-guest-experience'
    },
    {
      type: 'template',
      title: 'Hotel Concierge Scripts',
      description: 'Pre-built voice scripts for common guest requests',
      url: '/templates/hotel-concierge'
    }
  ],

  faqs: [
    {
      question: 'How do guests access the Voice Genie?',
      answer: 'Guests can access Help Genie via QR codes placed in rooms, lobbies, and common areas. They simply scan with their phone and start speaking—no app download required. You can also integrate with your existing phone system.'
    },
    {
      question: 'Does it support multiple languages?',
      answer: 'Yes, Help Genie supports over 30 languages and can automatically detect and respond in the guest\'s preferred language, making it ideal for international travelers.'
    },
    {
      question: 'Can it integrate with our property management system?',
      answer: 'Absolutely. Help Genie integrates with popular PMS platforms like Opera, Cloudbeds, and Mews to access reservation details, process requests, and update guest records automatically.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "Five-star service\nat three in the morning",
      subtitle: "See how your Genie handles real guest interactions — making recommendations, fulfilling requests, and creating memorable experiences around the clock.",
    },
    chatScenarios: [
      {
        id: 'restaurant_recommendation',
        title: 'Concierge Request',
        messages: [
          { type: 'user' as const, text: "We just arrived and we're starving. Can you recommend a great Italian restaurant within walking distance?", delay: 300 },
          { type: 'genie' as const, text: "Welcome! For Italian within walking distance, I'd recommend Trattoria Bella — it's just two blocks east on Main Street. Excellent handmade pasta and a great wine list. Would you like me to make a reservation?", delay: 1200 },
          { type: 'user' as const, text: "Yes please! Table for two at 7:30 tonight", delay: 2500 },
          { type: 'genie' as const, text: "I've reserved a table for two at Trattoria Bella for 7:30 PM tonight. Mention you're staying at our hotel — they offer a complimentary dessert for guests. Enjoy your evening!", delay: 3400 },
          { type: 'user' as const, text: "That's amazing, thank you!", delay: 4600 },
          { type: 'genie' as const, text: "My pleasure! If you'd like, I can also recommend some after-dinner cocktail spots nearby. Just ask anytime. Have a wonderful dinner!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Personalized recommendation made', delay: 6500 },
          { id: '2', text: 'Restaurant reservation booked', delay: 6700 },
          { id: '3', text: 'Hotel partner benefit mentioned', delay: 6900 },
          { id: '4', text: 'Proactive upsell offered', delay: 7100 },
          { id: '5', text: 'Guest delight moment created', delay: 7300 },
        ],
      },
      {
        id: 'room_service',
        title: 'Room Service',
        messages: [
          { type: 'user' as const, text: "Hi, can I order room service? I'd like a club sandwich and a pot of coffee.", delay: 300 },
          { type: 'genie' as const, text: "Of course! A club sandwich and pot of coffee — great choice. Would you like the sandwich on white, wheat, or sourdough? And any dietary preferences I should note?", delay: 1200 },
          { type: 'user' as const, text: "Sourdough please, and can you add a side of fruit instead of fries?", delay: 2500 },
          { type: 'genie' as const, text: "Absolutely — club on sourdough with a fresh fruit side and a pot of coffee. That comes to $28. Which room should I deliver to?", delay: 3400 },
          { type: 'user' as const, text: "Room 412. Can I charge it to the room?", delay: 4600 },
          { type: 'genie' as const, text: "Charged to room 412. Your order will be delivered in approximately 25–30 minutes. Is there anything else I can get for you?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Order taken with customizations', delay: 6500 },
          { id: '2', text: 'Dietary preferences noted', delay: 6700 },
          { id: '3', text: 'Pricing provided transparently', delay: 6900 },
          { id: '4', text: 'Charged to room — no friction', delay: 7100 },
          { id: '5', text: 'Delivery estimate provided', delay: 7300 },
        ],
      },
      {
        id: 'late_checkout',
        title: 'Late Checkout',
        messages: [
          { type: 'user' as const, text: "My flight isn't until 4 PM tomorrow. Is there any way I can get a late checkout?", delay: 300 },
          { type: 'genie' as const, text: "I'd be happy to check on that for you. Let me look at availability for your room type tomorrow. What room are you in?", delay: 1200 },
          { type: 'user' as const, text: "Room 612, checking out tomorrow", delay: 2500 },
          { type: 'genie' as const, text: "Good news! I can offer you a late checkout until 2 PM at no extra charge, or until 4 PM for a half-day rate of $75. Given your flight time, the 2 PM option should work well. Which would you prefer?", delay: 3400 },
          { type: 'user' as const, text: "2 PM free late checkout is perfect!", delay: 4600 },
          { type: 'genie' as const, text: "Done — you're confirmed for a 2 PM checkout from room 612. I'll have housekeeping adjusted. Would you like me to arrange an airport shuttle for 2:15 PM?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Room availability checked', delay: 6500 },
          { id: '2', text: 'Options presented with pricing', delay: 6700 },
          { id: '3', text: 'Late checkout confirmed', delay: 6900 },
          { id: '4', text: 'Housekeeping schedule updated', delay: 7100 },
          { id: '5', text: 'Transportation proactively offered', delay: 7300 },
        ],
      },
      {
        id: 'activity_booking',
        title: 'Activity Booking',
        messages: [
          { type: 'user' as const, text: "We're here with the kids for the week. What kind of activities or tours do you recommend?", delay: 300 },
          { type: 'genie' as const, text: "How fun! For families, our most popular options are the morning snorkeling excursion, the rainforest zipline tour, and the sunset catamaran cruise. How old are your kids?", delay: 1200 },
          { type: 'user' as const, text: "8 and 12. They'd love the zipline!", delay: 2500 },
          { type: 'genie' as const, text: "The zipline is perfect for that age range — kids love it! The tour runs Tuesday and Thursday mornings, includes hotel pickup, and costs $85 per person. Family of four would be $340. Want me to book Thursday?", delay: 3400 },
          { type: 'user' as const, text: "Yes, Thursday for all four of us. The Johnson family.", delay: 4600 },
          { type: 'genie' as const, text: "Booked! Zipline tour Thursday at 9 AM for the Johnson family — pickup from the lobby at 8:30 AM. Wear closed-toe shoes and sunscreen. The kids are going to have a blast!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Age-appropriate activities recommended', delay: 6500 },
          { id: '2', text: 'Pricing and logistics provided', delay: 6700 },
          { id: '3', text: 'Tour booked for full family', delay: 6900 },
          { id: '4', text: 'Pickup details confirmed', delay: 7100 },
          { id: '5', text: 'Ancillary revenue generated ($340)', delay: 7300 },
        ],
      },
      {
        id: 'maintenance_request',
        title: 'Room Issue',
        messages: [
          { type: 'user' as const, text: "It's 11 PM and the AC in our room isn't working. It's really hot in here.", delay: 300 },
          { type: 'genie' as const, text: "I'm sorry about that — let me help right away. First, can you check if the thermostat display is on? Sometimes it needs a quick reset. Try pressing the power button off and on.", delay: 1200 },
          { type: 'user' as const, text: "The display is blank, nothing happens when I press buttons", delay: 2500 },
          { type: 'genie' as const, text: "That sounds like a power issue with the unit. I've alerted our maintenance team — they'll be at your room within 10 minutes. In the meantime, I can bring up a portable fan and some cold water.", delay: 3400 },
          { type: 'user' as const, text: "Thank you, that would be great. Room 815.", delay: 4600 },
          { type: 'genie' as const, text: "A fan and cold water are on the way to room 815, and maintenance right behind them. I sincerely apologize for the inconvenience. If it can't be resolved quickly, I'll arrange an immediate room move.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Remote troubleshooting attempted', delay: 6500 },
          { id: '2', text: 'Maintenance dispatched (10 min)', delay: 6700 },
          { id: '3', text: 'Interim comfort provided', delay: 6900 },
          { id: '4', text: 'Room move contingency planned', delay: 7100 },
          { id: '5', text: 'Guest recovery initiated', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Elevate Every Guest Experience",
      subtitle: "Your guests expect five-star service at every hour. Your Genie delivers concierge-level assistance, instant answers, and proactive upsells — without adding staff.",
    },
    useCaseCards: [
      {
        id: 'virtual-concierge',
        title: 'Virtual Concierge',
        description: "Restaurant recommendations, local tips, directions, event bookings — your Genie gives every guest a personal concierge, 24 hours a day.",
        bullets: [
          'Local restaurant and activity recommendations',
          'Instant reservation booking',
          'Directions and transportation help',
          'Personalized to guest preferences',
        ],
        image: '/solutions-pages/travel-hospitality/usecase-virtual-concierge.webp',
      },
      {
        id: 'room-service-requests',
        title: 'Room Service & Requests',
        description: "Food orders, extra towels, pillow menus, housekeeping — guests ask your Genie instead of waiting on hold for the front desk.",
        bullets: [
          'Voice-activated room service ordering',
          'Housekeeping and maintenance requests',
          'Special amenity requests',
          'Charged directly to room',
        ],
        image: '/solutions-pages/travel-hospitality/usecase-room-service.webp',
      },
      {
        id: 'tour-upsells',
        title: 'Tours & Activity Booking',
        description: "Your Genie recommends and books excursions, spa treatments, and dining — generating ancillary revenue around the clock.",
        bullets: [
          'Age and interest-appropriate recommendations',
          'Instant booking with confirmation',
          'Partner activity commissions captured',
          '25%+ increase in ancillary revenue',
        ],
        image: '/solutions-pages/travel-hospitality/usecase-tour-booking.webp',
      },
      {
        id: 'reservation-management',
        title: 'Reservation Management',
        description: "Date changes, room upgrades, late checkouts, early arrivals — your Genie handles modifications instantly without front desk queues.",
        bullets: [
          'Real-time availability checking',
          'Upgrade and upsell opportunities',
          'Late checkout processing',
          'Confirmation sent instantly',
        ],
        image: '/solutions-pages/travel-hospitality/usecase-reservation-mgmt.webp',
      },
    ],
    finalCTA: {
      title: "Your Concierge Never Sleeps",
      subtitle: "At 3 AM when a guest wants restaurant tips, at 6 AM when they need an airport shuttle, at midnight when the AC is out — your Genie delivers five-star service, every time.",
      pillars: [
        {
          title: 'Always\nWelcoming',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Every guest gets concierge-level service — from check-in to checkout.',
        },
        {
          title: 'Always\nSelling',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Tours, spa, dining, upgrades — booked proactively, generating revenue 24/7.',
        },
        {
          title: 'Always\nDelighting',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Five-star reviews earned through instant, thoughtful, personalized service.',
        },
      ],
      backgroundImage: '/solutions-pages/travel-hospitality/cta-background.webp',
    },
  }
};
